import { useState } from 'react';
import { OrderList } from '../../../../Component/Interface/Order';

export const TEST_ORDER_SEQ = -175001;
export const TEST_DETAIL_SEQ = -175001;
const key = 'fanbird.order-list-test.175';
const displayDate = (value: string) => new Date(value).toLocaleString('sv-SE', { timeZone: 'Asia/Seoul', hour12: false });
type State = { status: string; cancelled: number; refunded: number; created: string; updated: string; memo: string };
const initial = (): State => ({ status: '결제대기', cancelled: 0, refunded: 0, created: new Date().toISOString(), updated: new Date().toISOString(), memo: '' });
function read(): State {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (value && ['결제대기', '결제완료', '배송완료', '주문취소'].includes(value.status) && Number.isSafeInteger(value.cancelled)
      && value.cancelled >= 0 && value.cancelled <= 3 && Number.isSafeInteger(value.refunded) && value.refunded >= 0
      && value.refunded <= value.cancelled * 10000 && Number.isFinite(Date.parse(value.created)) && Number.isFinite(Date.parse(value.updated))) return value;
  } catch { /* A cleared or unavailable session starts a fresh local test. */ }
  return initial();
}

export default function useRefundTestOrder(liveSeq: number) {
  const enabled = process.env.NODE_ENV === 'development' && liveSeq === 175;
  const [state, setState] = useState<State>(read);
  const save = (next: State) => {
    setState(next);
    try { sessionStorage.setItem(key, JSON.stringify(next)); } catch { /* The current test still works without persistence. */ }
  };
  const status = state.cancelled === 3 ? '환불처리' : state.status;
  const remaining = 3 - state.cancelled;
  const paid = ['결제완료', '배송완료'].includes(state.status);
  const matches = (name: string) => enabled && (name === '전체' || (name === '환불' ? state.cancelled > 0 : name === status));
  const order: OrderList = {
    seq: TEST_ORDER_SEQ, userid: 'local-refund-test', order_name: '[테스트] 주문자', order_nickname: '환불시험',
    addr_basic: '테스트 주소', addr_detail: '', addr_num: '00000', addr_phone: '01000000000', addr_recipient: '[테스트] 주문자',
    order_dt: displayDate(state.created), order_inquiry: null, total_payment: remaining * 10000, delivery_price: 0, matching: false,
    payment_summary: { payment_mode: 'pg', pg_amount: remaining * 10000, bank_amount: 0, pg_paid: paid, bank_paid: true,
      pg_due_amount: paid ? 0 : remaining * 10000, bank_due_amount: 0, pg_paid_at: paid ? state.created : null, bank_paid_at: null,
      deposit_deadline: null, deposit_attention: false, deposit_overdue: false, bank_account: null },
    order_details: [{ detail_seq: TEST_DETAIL_SEQ, product_seq: TEST_DETAIL_SEQ, product_num: 175001, product_name: '[테스트] 티셔츠',
      option_name: '기본', option_seq: null, detail_order_dt: displayDate(state.created), order_cnt: 3, product_price: 10000, order_price: 30000,
      refund_unit_settlement: '[{"count":3,"net":10000}]', refund_unit_offset: state.cancelled,
      cancel_cnt: state.cancelled, cancel_price: state.cancelled * 10000, order_status: status, status_update_dt: displayDate(state.updated),
      delivery_info: null, refund_memo: null, payment_method: 'pg', admin_memo: state.memo,
      ...(state.cancelled > 0 ? { refund_summary: { quantity: state.cancelled, amount: state.refunded, completed_amount: state.refunded,
        pending_amount: 0, pending_quantity: 0, registered_at: displayDate(state.updated) } } : {}) }],
  };
  return { enabled, order, matches, revision: JSON.stringify(state),
    counts: enabled ? { [status]: 1, ...(state.cancelled > 0 ? { 환불: 1 } : {}), 전체: 1 } : {},
    reset: () => save(initial()),
    memo: (memo: string) => save({ ...state, memo }),
    changeStatus: (next: string) => {
      if (!enabled || !['결제대기', '결제완료', '배송완료', '주문취소'].includes(next)) throw new Error('테스트 주문은 결제완료·배송완료 처리와 취소·환불 시험에 사용해주세요.');
      if (state.status === '주문취소' && next !== '결제대기') throw new Error('취소된 테스트 주문은 결제대기로 복원한 뒤 처리해주세요.');
      save({ ...state, status: next, updated: new Date().toISOString() });
    },
    refund: (quantity: number, amount: number) => {
      if (!enabled || !paid || !Number.isSafeInteger(quantity) || quantity < 1 || quantity > remaining
        || !Number.isSafeInteger(amount) || amount < 0 || amount > quantity * 10000) throw new Error('테스트 환불 수량과 금액을 확인해주세요.');
      save({ ...state, cancelled: state.cancelled + quantity, refunded: state.refunded + amount, updated: new Date().toISOString() });
    },
  };
}
